'use client';

import { analytics } from '@kit/analytics';

type AuditionExternalLinkProps = React.ComponentPropsWithoutRef<'a'> & {
  auditionId: string;
  /**
   * Which off-site destination this anchor points at:
   * - `apply` — `auditions.external_link`, the organizer's application page.
   * - `source` — `auditions.source_url`, the original posting an imported
   *   audition was extracted from.
   */
  linkType: 'apply' | 'source';
};

/**
 * Outbound anchor for an audition's off-site destination, instrumented with
 * the `audition_external_click` analytics event.
 *
 * 337 of 374 auditions carry an `external_link`, so following it is where the
 * aggregator funnel ends — nothing downstream of the audition page is
 * measurable without this event. Every off-site audition anchor routes through
 * here so the event has exactly ONE emitter (finding F7,
 * `docs/wip/active/WIP_remediating_seo_and_growth_audit_findings_2026_07_26.md`).
 *
 * `target="_blank"` keeps the current tab alive, so a plain click handler is
 * sufficient — there is no unload race that would need `sendBeacon`.
 *
 * Renders a bare `<a>`, so it drops into `<Button asChild>` in place of the
 * anchor it replaces (Radix `Slot` merges `className` through `...rest`).
 */
export function AuditionExternalLink({
  auditionId,
  linkType,
  href,
  onClick,
  children,
  ...rest
}: AuditionExternalLinkProps) {
  return (
    <a
      {...rest}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => {
        void analytics.trackEvent('audition_external_click', {
          audition_id: auditionId,
          link_type: linkType,
          destination_host: destinationHost(href),
        });

        onClick?.(event);
      }}
    >
      {children}
    </a>
  );
}

/**
 * Host of the destination, so the funnel can be split by application platform
 * (DanceApply / Acceptd / the organizer's own site) without shipping the full
 * URL — which can carry organizer query params — into the analytics payload.
 */
function destinationHost(href: string | undefined) {
  if (!href) return 'unknown';

  try {
    return new URL(href).host;
  } catch {
    return 'unknown';
  }
}
